import { throwApiError } from '@/shared/utils/apiResponse';
import { toMovimentarPendenciasBody } from '@/shared/utils/pendenciasMovimentacao';
import { apiDelete, apiGet, apiPost, request } from '@/shared/utils/requestBuilder';
import type {
  MovimentarPendenciasPayload,
  MovimentarPendenciasResultado,
} from '@/types/processoArbitral/pendenciasMovimentacao';

import type { InternoAndamento, InternoPendencia, InternoPendenciasResult } from '../../types';
import { jsonOk } from '../jsonOk';

const asAndamento = (raw: unknown): InternoAndamento | null => {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.id !== 'string') return null;
  const autor =
    typeof rec.autor === 'string'
      ? rec.autor
      : rec.autor && typeof rec.autor === 'object' && 'nome' in rec.autor
        ? String((rec.autor as { nome?: unknown }).nome ?? '')
        : '';
  return {
    id: rec.id,
    descricao: typeof rec.descricao === 'string' ? rec.descricao : '',
    dataHora:
      typeof rec.dataHora === 'string'
        ? rec.dataHora
        : typeof rec.dataCriacao === 'string'
          ? rec.dataCriacao
          : '',
    autor,
    chatMensagemId: typeof rec.chatMensagemId === 'string' ? rec.chatMensagemId : null,
  };
};

export const processoInternoService = {
  listAndamentos: async (processoId: string): Promise<InternoAndamento[]> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/andamentos`);
    const data = await jsonOk<unknown>(response, 'Erro ao carregar andamentos');
    const items = Array.isArray(data)
      ? data
      : data && typeof data === 'object' && 'items' in data
        ? (data as { items: unknown }).items
        : [];
    return (Array.isArray(items) ? items : []).flatMap((item) => {
      const parsed = asAndamento(item);
      return parsed ? [parsed] : [];
    });
  },

  tornarAndamento: async (conversaId: string, mensagemId: string): Promise<InternoAndamento> => {
    const response = await apiPost(`/conversas/${conversaId}/mensagens/${mensagemId}/andamento`);
    const parsed = asAndamento(await jsonOk<unknown>(response, 'Erro ao tornar andamento'));
    if (!parsed) throw new Error('Resposta inválida ao tornar andamento');
    return parsed;
  },

  removerSelo: async (conversaId: string, mensagemId: string): Promise<void> => {
    const response = await apiDelete(`/conversas/${conversaId}/mensagens/${mensagemId}/andamento`);
    if (!response.ok) await throwApiError(response, 'Erro ao remover andamento');
  },

  excluirAndamento: async (
    processoId: string,
    andamentoId: string,
    motivo?: string,
  ): Promise<void> => {
    const endpoint = `/admin/arbitral/processos/${processoId}/andamentos/${andamentoId}`;
    const response = motivo
      ? await request({ endpoint, method: 'DELETE', body: { motivo } })
      : await apiDelete(endpoint);
    if (!response.ok) await throwApiError(response, 'Erro ao excluir andamento');
  },

  listPendencias: async (
    processoId: string,
    filtro: 'todas' | 'ativas' | 'finalizadas' = 'todas',
  ): Promise<InternoPendenciasResult> => {
    const response = await apiGet(`/admin/arbitral/processos/${processoId}/pendencias`, { filtro });
    const data = await jsonOk<Partial<InternoPendenciasResult> & { items?: InternoPendencia[] }>(
      response,
      'Erro ao carregar pendências',
    );
    const items = data.items ?? [];
    return {
      items,
      total: data.total ?? items.length,
      contagem: data.contagem,
    };
  },

  /**
   * O endpoint responde 200 mesmo quando movimenta só parte do lote, então o corpo precisa ser
   * lido: descartá-lo fazia a aba anunciar "Pendência respondida." para pendência recusada.
   */
  responderPendencias: async (
    payload: MovimentarPendenciasPayload,
  ): Promise<MovimentarPendenciasResultado> => {
    const response = await apiPost(
      '/admin/arbitral/pendencias/movimentacoes',
      toMovimentarPendenciasBody(payload),
    );
    const data = await jsonOk<Partial<MovimentarPendenciasResultado>>(
      response,
      'Erro ao responder pendência',
    );
    // Sem as duas listas não dá para saber o que o core aceitou: o `?? []` anterior lia ausência
    // como "nada recusado" e a aba anunciava sucesso — pior ainda em `movimentadas`, onde o
    // reducer mantinha a pendência na lista e a tela dizia que ela tinha sido respondida.
    if (!Array.isArray(data.movimentadas) || !Array.isArray(data.naoMovimentadas)) {
      throw new Error('Resposta inválida ao responder pendência');
    }
    return {
      movimentadas: data.movimentadas,
      naoMovimentadas: data.naoMovimentadas,
      andamentoId: data.andamentoId ?? null,
    };
  },
};
